
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Check, AlertCircle, ShieldCheck } from "lucide-react";

interface SubscriptionStatusProps {
  status: "active" | "inactive";
  plan?: "monthly" | "yearly" | null;
  nextBillingDate?: string | null;
  subscriptionId?: string | null;
  isCancelled?: boolean;
  onPremiumAction: () => void;
}

const SubscriptionStatus = ({ 
  status, 
  plan = "monthly", 
  nextBillingDate, 
  subscriptionId,
  isCancelled = false,
  onPremiumAction 
}: SubscriptionStatusProps) => {
  // Format the billing date if it exists
  const formattedBillingDate = nextBillingDate ? 
    new Date(nextBillingDate).toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    }) : null;
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start mb-1">
        <div className="flex items-center gap-2">
          {status === "active" ? (
            <>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1 px-2 py-1">
                <Check className="w-3 h-3" /> Active
              </Badge>
              <span className="text-[#4A5568] text-sm">
                {plan === "monthly" ? "Monthly Plan" : "Yearly Plan"}
              </span>
            </>
          ) : (
            <Badge variant="outline" className="bg-gray-100 text-gray-700 px-2 py-1">
              Inactive
            </Badge>
          )}
        </div>
      </div>
      
      {status === "active" && (
        <>
          {isCancelled && formattedBillingDate && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                Your subscription has been cancelled and will expire on {formattedBillingDate}
              </span>
            </div>
          )}
          
          {formattedBillingDate && !isCancelled && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 text-blue-700 text-sm">
              <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Premium Plan Active</p>
                <p className="text-xs mt-0.5">You have access to all premium features</p>
              </div>
            </div>
          )}
          
          {formattedBillingDate && (
            <div className="flex items-center gap-2 text-sm text-[#4A5568] mt-4">
              <CalendarDays className="w-4 h-4" />
              <span>
                {isCancelled ? "Access until" : "Next billing date"}: {formattedBillingDate}
              </span>
            </div>
          )}
        </>
      )}
      
      <Button 
        onClick={onPremiumAction}
        variant={status === "active" ? "outline" : "default"}
        className={status === "active" 
          ? "border-[#607D9B] text-[#607D9B] hover:bg-[#F3F7FC] w-full mt-4" 
          : "bg-[#517FA4] hover:bg-[#406584] text-white w-full mt-4"
        }
      >
        {status === "active" ? "Manage Subscription" : "Get Premium"}
      </Button>
    </div>
  );
};

export default SubscriptionStatus;
